import { useEffect } from 'react';
import { SITE_URL, SITE_NAME } from './MetaTags';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

const toIso = (value) => {
  if (!value) return null;
  const m = String(value).match(ISO_DATE);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
};

const absoluteUrl = (path, base) => {
  if (!path) return base;
  if (/^https?:\/\//i.test(path)) return path;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const buildAuthor = (author) => {
  if (!author || typeof author !== 'object') {
    return { '@type': 'Organization', name: SITE_NAME };
  }
  return {
    '@type': 'Person',
    name: author.name || SITE_NAME,
    ...(author.credentials ? { description: author.credentials } : {})
  };
};

/**
 * FAQSchema owns question/answer data. Guides, including legacy HowTo
 * frontmatter, use Article until structured steps are available from
 * the visible content; do not emit an empty procedure.
 */
const buildSchema = ({
  schemaType,
  title,
  description,
  canonical,
  author,
  datePublished,
  dateModified,
  image,
  keywords
}) => {
  const url = absoluteUrl(canonical, SITE_URL);
  const finalImage = absoluteUrl(image || '/images/og-image.svg', SITE_URL);

  if (schemaType === 'FAQPage') {
    // The actual Question/Answer list is injected by the FAQSchema
    // component, which consolidates all <FAQSchema> instances on the
    // page into a single block. We skip the page-level envelope here
    // so we don't emit two FAQPage blocks.
    return null;
  }

  // Default: Article
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    image: finalImage,
    author: buildAuthor(author),
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.svg` }
    },
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    ...(keywords && keywords.length ? { keywords: keywords.join(', ') } : {})
  };
};

/**
 * Injects a route-specific Article/HowTo/FAQPage JSON-LD block into the
 * document head, and removes itself on unmount. Safe to mount multiple
 * times — only one block tagged with `data-ivt-article-schema` is kept.
 */
const ArticleSchema = ({
  schemaType = 'Article',
  title,
  description,
  canonical,
  author,
  datePublished,
  dateModified,
  image,
  keywords
}) => {
  useEffect(() => {
    const payload = buildSchema({
      schemaType,
      title,
      description,
      canonical,
      author,
      datePublished,
      dateModified,
      image,
      keywords
    });
    if (!payload) return undefined;
    // Idempotent: if a script tag is already in the document (for example,
    // captured during prerendering), do not add a second one.
    if (document.querySelector('script[data-ivt-article-schema="true"]')) {
      return undefined;
    }
    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.setAttribute('data-ivt-article-schema', 'true');
    tag.text = JSON.stringify(payload);
    document.head.appendChild(tag);
    return () => {
      tag.remove();
    };
  }, [
    schemaType,
    title,
    description,
    canonical,
    author,
    datePublished,
    dateModified,
    image,
    keywords
  ]);
  return null;
};

export { buildSchema, toIso };
export default ArticleSchema;
